import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppLocalization } from '@kaltura-ng/mc-shared';
import { BrowserService } from 'app-shared/kmc-shell/providers/browser.service';
import {
  KalturaClient,
  KalturaMediaEntry,
  KalturaEntryStatus,
  KalturaEntryReplacementStatus,
  KalturaExternalMediaEntry,
  KalturaMediaType,
  KalturaLiveEntry,
} from 'kaltura-ngx-client';
import { KalturaLogger } from '@kaltura-ng/kaltura-logger';
import { KmcComponentViewBaseService } from 'app-shared/kmc-shared/kmc-views/kmc-component-view-base.service';
import { serverConfig } from 'config/server';

import { KMCPermissionsService, KMCPermissions } from '../../kmc-permissions';

export interface HotspotsAppViewArgs {
  entry: KalturaMediaEntry | KalturaLiveEntry;
  hasSource: boolean;
}

@Injectable()
export class HotspotsAppViewService extends KmcComponentViewBaseService<HotspotsAppViewArgs> {
  constructor(
    private _appPermissions: KMCPermissionsService,
    private _appLocalization: AppLocalization,
    private _kalturaClient: KalturaClient,
    private _router: Router,
    _browserService: BrowserService,
    _logger: KalturaLogger,
  ) {
    super(_logger.subLogger('HotspotsAppViewService'));
  }

  isAvailable(args: HotspotsAppViewArgs): boolean {
    const availableByConfiguration = !!serverConfig.externalApps.editor;
    const availableByPermissions = this._isAvailableByPermission();
    const availableByData = this._isAvailableByData(args);
    const result = availableByConfiguration && availableByData && availableByPermissions;
    this._logger.info(`handle isAvailable action`, {
      availableByConfiguration,
      availableByPermissions,
      availableByData,
      result,
    });
    return result;
  }

  private _isAvailableByPermission(): boolean {
    return true;
  }

  private _isAvailableByData(args: HotspotsAppViewArgs): boolean {
    const { entry, hasSource } = args;
    const entryReady = entry.status === KalturaEntryStatus.ready;
    const isEntryReplacing = entry.replacementStatus !== KalturaEntryReplacementStatus.none;
    const isExternalMedia = entry instanceof KalturaExternalMediaEntry;
    const isEntryRelevant = [KalturaMediaType.video].indexOf(entry.mediaType) !== -1 && !isExternalMedia;
    const isLiveEntry =
      [
        KalturaMediaType.liveStreamFlash,
        KalturaMediaType.liveStreamWindowsMedia,
        KalturaMediaType.liveStreamRealMedia,
        KalturaMediaType.liveStreamQuicktime,
      ].indexOf(entry.mediaType) !== -1;
    const isAvailableForMedia = !isLiveEntry && isEntryRelevant && hasSource && entryReady && !isEntryReplacing;
    const result = isAvailableForMedia;

    this._logger.trace(`conditions used to check availability status by data`, () => ({
      result,
      entryReady,
      hasSource,
      isLiveEntry,
      isEntryReplacing,
      isExternalMedia,
      entryMediaType: entry.mediaType,
      isEntryRelevant,
      isAvailableForMedia,
    }));

    return result;
  }
}
